import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QuestionnaireFormData } from '../features/Questionnaire/QuestionnaireCRUD.utils';
import { backendServices } from '../services';
import { BusinessDocType, FieldDictionaryUsedBy } from '../types';

const QUESTIONNAIRE_SKELETON_CLIENT = FieldDictionaryUsedBy.CSP;

type SkeletonData = Awaited<ReturnType<InstanceType<typeof backendServices.Apis.DocumentApi>['getSkeleton']>>['data'];

interface QuestionnaireSkeletonContext {
    buyerCompanyId: number;
    supplierCompanyId: number;
}

/**
 * Fetches the SIM document skeleton for each questionnaire in parallel.
 *
 * Single-skeleton fetches go through `useDocumentSkeleton`; this hook
 * exists because the questionnaire count is dynamic, which rules out
 * calling `useDocumentSkeleton` in a `.map(...)` (rules of hooks).
 * `useQueries` keeps the hook count constant while batching an N-length
 * list of skeleton fetches.
 */
export function useQuestionnaireSimSkeletons(
    questionnaires: QuestionnaireFormData[],
    context: QuestionnaireSkeletonContext | null
) {
    const queries = useQueries({
        queries: questionnaires.map((questionnaire) => {
            const businessDocSubTypeId = questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId;

            return {
                queryKey: [
                    'QuestionnaireSimSkeleton',
                    context?.buyerCompanyId,
                    context?.supplierCompanyId,
                    businessDocSubTypeId,
                ],
                queryFn: async () => {
                    const api = new backendServices.Apis.DocumentApi();
                    const { data } = await api.getSkeleton({
                        businessDocType: BusinessDocType.SIMDocument,
                        client: QUESTIONNAIRE_SKELETON_CLIENT,
                        buyerCompanyId: context!.buyerCompanyId,
                        supplierCompanyId: context!.supplierCompanyId,
                        documentClass: businessDocSubTypeId!,
                    });

                    return {
                        businessDocSubTypeId: businessDocSubTypeId!,
                        skeleton: data,
                    };
                },
                enabled: !!context && businessDocSubTypeId != null,
                staleTime: Infinity,
                cacheTime: Infinity,
            };
        }),
    });

    const skeletonsBySubTypeId = useMemo(() => {
        const map: Record<number, SkeletonData> = {};

        queries.forEach((query) => {
            if (query.data) {
                map[query.data.businessDocSubTypeId] = query.data.skeleton;
            }
        });

        return map;
    }, [queries]);

    const isLoading = queries.some((query) => query.isLoading);
    const isError = queries.some((query) => query.isError);

    return {
        skeletonsBySubTypeId,
        isLoading,
        isError,
    };
}
